import { NgClass } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  type ElementRef,
  inject,
  Injector,
  input,
  type OnDestroy,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { AssessedWord, PronunciationAssessment } from '../../../models/quiz';
import {
  PronunciationSpeechService,
  SLOW_PLAYBACK_RATE,
} from '../../../services/pronunciation-speech/pronunciation-speech';
import { GuidedTourAnchorDirective } from '../../guided-tour/guided-tour-anchor';
import { PronunciationWordDetailComponent } from './pronunciation-word-detail/pronunciation-word-detail';
import { buildWordDetail, toWordStyle, WORD_RESULT_LABELS, type WordStyle } from './word-breakdown';

export interface PronunciationWord {
  /** The token as written, punctuation included. */
  text: string;
  style: WordStyle;
  /** The token without its edge punctuation, which is what is read aloud. */
  speechText: string;
  playbackId: string;
  /** Its position among the focusable items of the sentence, after the play button and the turtle. */
  itemIndex?: number;
  labelKey: string;
  resultLabel?: string;
}

const SENTENCE_PLAYBACK_ID = 'sentence';
const SLOW_SENTENCE_PLAYBACK_ID = 'sentence-slow';
const SPEECH_ITEM_SELECTOR = '[data-speech-item]';
const EDGE_PUNCTUATION = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
const WHITESPACE = /\s+/;
const SLOW_ITEM_INDEX = 1;
const FIRST_WORD_ITEM_INDEX = 2;

let nextInstanceId = 0;

/**
 * The sentence of a voice question coloured by how each word was pronounced. When the book language
 * can be read aloud, a play button plays the whole sentence, a turtle plays it slower, and each word plays itself and opens its
 * detail below the sentence. Words are split on whitespace, as Exercises assesses them, and matched
 * with the assessment by index.
 */
@Component({
  selector: 'nr-pronunciation-feedback',
  imports: [NgClass, TranslatePipe, GuidedTourAnchorDirective, PronunciationWordDetailComponent],
  templateUrl: './pronunciation-feedback.html',
  styleUrl: './pronunciation-feedback.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(keydown)': 'onKeydown($event)',
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PronunciationFeedbackComponent implements OnDestroy {
  private readonly pronunciationSpeech = inject(PronunciationSpeechService);
  private readonly injector = inject(Injector);
  private readonly sentenceToolbar = viewChild<ElementRef<HTMLElement>>('sentenceToolbar');
  private readonly wordDetail = viewChild<ElementRef<HTMLElement>>('wordDetail');
  private readonly selectedWordIndex = signal<number | undefined>(undefined);

  /** The play button only shows once the sentence can be synthesised, so a failure hides it silently. */
  private readonly speechAvailability = resource({
    params: computed(() => {
      const text = this.sentenceText();
      if (!this.pronunciationSpeech.isSupported() || !text) return undefined;
      return { text, questionId: this.questionId() };
    }),
    loader: async ({ params: { text, questionId } }) =>
      this.pronunciationSpeech.load(text, questionId).then(
        () => true,
        () => false,
      ),
  });

  readonly assessment = input.required<PronunciationAssessment>();

  readonly text = input.required<string>();

  readonly questionId = input.required<string>();

  readonly hintId = `pronunciation-feedback-hint-${nextInstanceId}`;

  readonly listenId = `pronunciation-feedback-listen-${nextInstanceId}`;

  readonly slowId = `pronunciation-feedback-slow-${nextInstanceId}`;

  readonly wordDetailId = `pronunciation-feedback-detail-${nextInstanceId++}`;

  readonly slowItemIndex = SLOW_ITEM_INDEX;

  readonly activeItemIndex = signal(0);

  readonly sentenceText = computed(() => this.text().trim().replace(/\s+/g, ' '));

  readonly assessedWords = computed(() => this.buildAssessedWords());

  readonly lastItemIndex = computed(
    () =>
      SLOW_ITEM_INDEX +
      this.assessedWords().filter(({ itemIndex }) => itemIndex !== undefined).length,
  );

  readonly tabStopIndex = computed(() => Math.min(this.activeItemIndex(), this.lastItemIndex()));

  readonly speechAvailable = computed(() => this.speechAvailability.value() === true);

  readonly activePlayback = computed(() => {
    const playback = this.pronunciationSpeech.playback();
    return playback?.owner === this ? playback : undefined;
  });

  readonly isSentencePlaying = computed(() => this.activePlayback()?.id === SENTENCE_PLAYBACK_ID);

  readonly isSlowSentencePlaying = computed(
    () => this.activePlayback()?.id === SLOW_SENTENCE_PLAYBACK_ID,
  );

  readonly selectedIndex = this.selectedWordIndex.asReadonly();

  readonly selectedWord = computed(() => {
    const index = this.selectedWordIndex();
    return index === undefined ? undefined : this.assessedWords().at(index);
  });

  readonly selectedWordDetail = computed(() => {
    const index = this.selectedWordIndex();
    const word = this.selectedWord();
    if (index === undefined || !word) return undefined;
    return buildWordDetail({
      word: this.assessment().words.at(index),
      speechText: word.speechText,
      style: word.style,
    });
  });

  readonly isSelectedWordPlaying = computed(() => {
    const word = this.selectedWord();
    return Boolean(word) && this.activePlayback()?.id === word?.playbackId;
  });

  ngOnDestroy(): void {
    this.pronunciationSpeech.stop(this);
  }

  toggleSentence(): void {
    if (this.isSentencePlaying()) {
      this.pronunciationSpeech.stop(this);
      return;
    }
    this.playText(this.sentenceText(), SENTENCE_PLAYBACK_ID);
  }

  /** Plays the whole sentence slower, for students who need to follow each sound. */
  toggleSlowSentence(): void {
    if (this.isSlowSentencePlaying()) {
      this.pronunciationSpeech.stop(this);
      return;
    }
    this.playText(this.sentenceText(), SLOW_SENTENCE_PLAYBACK_ID, SLOW_PLAYBACK_RATE);
  }

  /** Plays the word and shows its detail below the sentence, scrolling the dialog to it. */
  openWord(index: number): void {
    const word = this.assessedWords().at(index);
    if (!word?.speechText) return;
    this.selectedWordIndex.set(index);
    this.playText(word.speechText, word.playbackId);
    afterNextRender(
      () => {
        this.wordDetail()?.nativeElement.scrollIntoView({ block: 'nearest' });
      },
      { injector: this.injector },
    );
  }

  playSelectedWord(): void {
    const word = this.selectedWord();
    if (word) this.playText(word.speechText, word.playbackId);
  }

  /** Closes the detail and gives the focus back to its word. */
  closeWordDetail(): void {
    const itemIndex = this.selectedWord()?.itemIndex;
    this.selectedWordIndex.set(undefined);
    if (itemIndex === undefined) return;
    this.activeItemIndex.set(itemIndex);
    this.getSpeechItems().at(itemIndex)?.focus();
  }

  /**
   * Escape closes the word detail only, so the voice question dialog stays open. Inside the
   * sentence, which is a single tab stop, arrows, Home and End move between its items.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeWordDetailOnEscape(event);
      return;
    }
    const toolbar = this.sentenceToolbar()?.nativeElement;
    if (!toolbar || !(event.target instanceof Node) || !toolbar.contains(event.target)) return;

    const items = this.getSpeechItems();
    const targetIndex = PronunciationFeedbackComponent.getTargetItemIndex(
      event.key,
      this.tabStopIndex(),
      items.length,
    );
    if (targetIndex === undefined) return;

    event.preventDefault();
    this.activeItemIndex.set(targetIndex);
    items.at(targetIndex)?.focus();
  }

  private closeWordDetailOnEscape(event: KeyboardEvent): void {
    if (this.selectedWordIndex() === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    this.closeWordDetail();
  }

  private getSpeechItems(): HTMLElement[] {
    const toolbar = this.sentenceToolbar()?.nativeElement;
    return toolbar ? Array.from(toolbar.querySelectorAll<HTMLElement>(SPEECH_ITEM_SELECTOR)) : [];
  }

  private playText(text: string, playbackId: string, rate?: number): void {
    this.pronunciationSpeech.play({
      text,
      questionId: this.questionId(),
      owner: this,
      id: playbackId,
      ...(rate && { rate }),
    });
  }

  private buildAssessedWords(): PronunciationWord[] {
    const { words } = this.assessment();
    let nextItemIndex = FIRST_WORD_ITEM_INDEX;
    return this.text()
      .trim()
      .split(WHITESPACE)
      .filter(Boolean)
      .map((token, index) => {
        const word = PronunciationFeedbackComponent.toPronunciationWord({
          token,
          index,
          word: words.at(index),
          itemIndex: nextItemIndex,
        });
        if (word.itemIndex !== undefined) nextItemIndex++;
        return word;
      });
  }

  private static toPronunciationWord({
    token,
    index,
    word,
    itemIndex,
  }: {
    token: string;
    index: number;
    word: AssessedWord | undefined;
    itemIndex: number;
  }): PronunciationWord {
    const style = toWordStyle(word);
    const speechText = token.replace(EDGE_PUNCTUATION, '');
    const resultLabel = WORD_RESULT_LABELS[style];
    return {
      text: token,
      style,
      speechText,
      playbackId: `word-${index}`,
      itemIndex: speechText ? itemIndex : undefined,
      labelKey: resultLabel
        ? 'QUESTIONS.PRONUNCIATION.LISTEN.WORD_WITH_RESULT'
        : 'QUESTIONS.PRONUNCIATION.LISTEN.WORD',
      resultLabel,
    };
  }

  private static getTargetItemIndex(
    key: string,
    currentIndex: number,
    itemCount: number,
  ): number | undefined {
    const lastIndex = itemCount - 1;
    if (key === 'ArrowRight') return Math.min(currentIndex + 1, lastIndex);
    if (key === 'ArrowLeft') return Math.max(currentIndex - 1, 0);
    if (key === 'Home') return 0;
    if (key === 'End') return lastIndex;
    return undefined;
  }
}
